import { Link } from "react-router-dom";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import { 
  ArrowRight, 
  CheckCircle, 
  Award,
  BookOpen,
  Users,
  Leaf,
  Brain,
  GraduationCap,
  Briefcase,
  Heart
} from "lucide-react";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const PHOTO_1 = `${BASE_URL}/alma-vivu/photos/apropos-maud-preparation.png`;
const PHOTO_2 = `${BASE_URL}/alma-vivu/photos/apropos-cartes-jeu.png`;
const PHOTO_3 = `${BASE_URL}/alma-vivu/photos/apropos-cartes-nature.png`;

const certifications = [
  "Management de la durabilité en entreprise (stratégie climat, gouvernance, trajectoires bas carbone).",
  "Design circulaire & Modèles d'affaires circulaires (éco-conception, pensée systémique, biomimétisme).",
  "Méthodologies d'évaluation carbone (Bilan Carbone\u00AE).",
  "Accompagnement à la transition bas carbone (méthode ACT Pas à Pas).",
];

const outils = [
  "Fresque de l'économie circulaire",
  "Fresque du Climat",
  "Fresque de la biodiversité",
  "Atelier 2 Tonnes",
  "Inventons nos vies bas carbone",
  "Pois Chiche Party",
  "Future of IA",
  "La bataille de l'IA",
  "La bataille de la tech",
  "Fresque de la mobilité",
  "Fresque des Frontières planétaires",
  "Super Loopi",
];

const engagements = [
  { icon: Brain, text: "Appréhender les enjeux systémiques de la transition écologique." },
  { icon: Leaf, text: "Ancrer l'esprit critique face aux fausses informations et au greenwashing." },
  { icon: Briefcase, text: "Faire évoluer les postures et les métiers vers plus de circularité et de responsabilité." },
  { icon: Users, text: "Co-construire des solutions concrètes adaptées au territoire insulaire." },
];

const DesignDurable = () => {
  const { getField } = usePageContent("design-durable");
  usePageTitle("A Propos");

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="design-durable-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--dusty-rose)] top-0 left-0"></div>
        <div className="blob-bg w-[400px] h-[400px] bg-[var(--maquis)]/10 bottom-0 right-0"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              A <span className="text-[var(--terracotta)] emphasis">Propos</span>
            </h1>
            <p className="text-xl text-[var(--stone)] mb-10 leading-relaxed">
              Transmettre, éclairer et donner les clés de l'action à tous les publics.
            </p>
          </div>
        </div>
      </section>

      {/* Qui suis-je + Photos */}
      <section className="pb-20" data-testid="who-section">
        <div className="container-premium">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12 items-start">
              {/* Photo side */}
              <div className="lg:col-span-2">
                <div className="rounded-3xl overflow-hidden shadow-xl aspect-[3/4]">
                  <img 
                    src={PHOTO_1} 
                    alt="Maud Dupupet — préparation d'atelier" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Text side */}
              <div className="lg:col-span-3 space-y-6">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                  Qui <span className="emphasis">suis-je</span> ?
                </h2>
                <p className="text-lg text-[var(--stone)] leading-relaxed">
                  Je suis <strong className="text-[var(--charcoal)]">Maud Dupupet</strong>, fondatrice d'Alma Vivu.
                </p>
                <p className="text-lg text-[var(--stone)] leading-relaxed">
                  Avec mes 17 ans d'expérience professionnelle au sein d'associations, de TPE, PME et grands groupes, j'ai choisi de mettre mes compétences au service des transitions écologique, économique et sociale de mon territoire.
                </p>
                <p className="text-lg text-[var(--stone)] leading-relaxed">
                  J'interviens auprès des établissements scolaires (écoles primaires, collèges et lycées), des centres de formation et des structures insulaires (centres aérés...) pour former les jeunes et les professionnels d'aujourd'hui et de demain.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photos gallery */}
      <section className="pb-20" data-testid="gallery-section">
        <div className="container-premium">
          <div className="max-w-5xl mx-auto grid grid-cols-2 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTO_2} alt="Cartes pédagogiques — outils d'animation" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTO_3} alt="Cartes nature — supports de formation" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Parcours & Expertises */}
      <section className="pb-20 bg-[var(--sand)]" data-testid="expertise-section">
        <div className="container-premium py-20">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Award className="w-7 h-7 text-[var(--terracotta)]" />
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                Mon parcours & mes <span className="emphasis">expertises</span>
              </h2>
            </div>

            <p className="text-lg text-[var(--stone)] leading-relaxed mb-10">
              Pour proposer un accompagnement rigoureux et fondé sur la science, je m'appuie sur un socle de formations et certifications de référence :
            </p>

            {/* Certifications */}
            <div className="space-y-4 mb-12">
              {certifications.map((cert, idx) => (
                <div key={idx} className="card-premium p-5 flex items-start gap-4" data-testid={`certification-${idx}`}>
                  <CheckCircle className="w-5 h-5 text-[var(--maquis)] mt-0.5 flex-shrink-0" />
                  <p className="text-[var(--charcoal)] leading-relaxed">{cert}</p>
                </div>
              ))}
            </div>

            {/* Outils de sensibilisation */}
            <div className="card-premium p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-6 h-6 text-[var(--terracotta)]" />
                <h3 className="font-display text-xl font-bold text-[var(--charcoal)]">
                  Outils de sensibilisation
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {outils.map((outil, idx) => (
                  <span 
                    key={idx} 
                    className="px-4 py-2 bg-[var(--bone)] rounded-full text-sm font-medium text-[var(--stone)] border border-[var(--sand)]"
                  >
                    {outil}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement formation */}
      <section className="pb-20 section-premium" data-testid="engagement-section">
        <div className="container-premium">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-7 h-7 text-[var(--terracotta)]" />
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                Mon engagement dans la <span className="emphasis">formation</span>
              </h2>
            </div>
            <p className="text-sm text-[var(--stone)] mb-10 uppercase tracking-wider">
              tout au long de la vie
            </p>

            <p className="text-lg text-[var(--stone)] leading-relaxed mb-10">
              En cohérence avec le cadre national de l'Éducation au Développement Durable et de la formation continue, j'aide les apprenants, enseignants, formateurs et professionnels à :
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              {engagements.map((item, idx) => (
                <div key={idx} className="card-premium p-6 flex items-start gap-4" data-testid={`engagement-${idx}`}>
                  <div className="w-10 h-10 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[var(--terracotta)]" />
                  </div>
                  <p className="text-[var(--stone)] leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-premium relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
        <div className="container-premium relative z-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)] mb-8">
            Prêt à transmettre<br />
            <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
          </h2>
          <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
            Faisons le point ensemble sur vos enjeux et construisons un parcours adapté à votre contexte et vos attentes.
          </p>
          <Link to="/contact">
            <button className="btn-primary text-lg px-10 py-5">
              Prendre rendez-vous
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DesignDurable;
